import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, MapPin, Phone, Mail, Globe, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";

const SERVICE_TYPES = [
  { value: "groomer", label: "Groomer" },
  { value: "trainer", label: "Trainer" },
  { value: "farrier", label: "Farrier" },
  { value: "boarding", label: "Boarding" },
  { value: "veterinary", label: "Veterinary" },
  { value: "transport", label: "Transport" },
];

const SPECIES_OPTIONS = [
  { value: "canine", label: "Canine (Dogs)" },
  { value: "feline", label: "Feline (Cats)" },
  { value: "equine", label: "Equine (Horses)" },
  { value: "cattle", label: "Cattle" },
  { value: "exotics", label: "Exotics (Birds, Miniature Primates)" },
  { value: "farm", label: "Farm Animals (Swine, Fowl, Goats, Sheep)" },
];

const VendorOnboarding = ({ user }) => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    display_name: "",
    description: "",
    service_type: "",
    species: [],
    location_city: "",
    location_state: "",
    location_zip: "",
    phone: "",
    email: user?.email || "",
    website: "",
  });

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.display_name || !form.service_type || form.species.length === 0) {
      toast.error("Please fill in the required fields (name, service type, and at least one species)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/service-providers", {
        kind: "service",
        ...form,
      });
      toast.success("Registration submitted! Your profile is pending admin verification.");
      navigate("/dashboard/vendor");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-2xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Service Provider</p>
        <h1 className="text-4xl text-[#EDEDED] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          Vendor Registration
        </h1>
        <p className="text-[#A1A1AA] mb-8">Register as a service provider on Breedling's marketplace.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="glass-card p-6 space-y-4">
            <h3 className="text-lg text-[#EDEDED] flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-[#D4AF37]" /> Business Details
            </h3>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1.5">Business Name *</label>
              <input
                value={form.display_name}
                onChange={(e) => update("display_name", e.target.value)}
                className="input-minimal w-full"
                placeholder="e.g., Smith's Grooming"
                data-testid="vendor-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">Service Type *</label>
                <select
                  value={form.service_type}
                  onChange={(e) => update("service_type", e.target.value)}
                  className="input-minimal w-full"
                  data-testid="service-type-select"
                >
                  <option value="">Select...</option>
                  {SERVICE_TYPES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">Species Served * <span className="text-[#71717A] text-xs">(select all that apply)</span></label>
                <div className="space-y-1.5 p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A] max-h-48 overflow-y-auto" data-testid="species-checkboxes">
                  {SPECIES_OPTIONS.map(s => (
                    <label key={s.value} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-[#18181B]">
                      <input
                        type="checkbox"
                        checked={form.species.includes(s.value)}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...form.species, s.value]
                            : form.species.filter(v => v !== s.value);
                          update("species", next);
                        }}
                        className="accent-[#D4AF37] w-4 h-4"
                        data-testid={`species-checkbox-${s.value}`}
                      />
                      <span className="text-sm text-[#EDEDED]">{s.label}</span>
                    </label>
                  ))}
                </div>
                {form.species.length > 0 && (
                  <p className="text-xs text-[#D4AF37] mt-1">{form.species.length} selected</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1.5">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                className="input-minimal w-full min-h-[80px]"
                placeholder="Describe your services..."
                data-testid="vendor-description"
              />
            </div>
          </div>

          <div className="glass-card p-6 space-y-4">
            <h3 className="text-lg text-[#EDEDED] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#D4AF37]" /> Location & Contact
            </h3>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">City</label>
                <input value={form.location_city} onChange={(e) => update("location_city", e.target.value)} className="input-minimal w-full" placeholder="City" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">State</label>
                <input value={form.location_state} onChange={(e) => update("location_state", e.target.value)} className="input-minimal w-full" placeholder="State" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5">ZIP</label>
                <input value={form.location_zip} onChange={(e) => update("location_zip", e.target.value)} className="input-minimal w-full" placeholder="ZIP" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone</label>
                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="input-minimal w-full" placeholder="(555) 123-4567" />
              </div>
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-1.5 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</label>
                <input value={form.email} onChange={(e) => update("email", e.target.value)} className="input-minimal w-full" placeholder="contact@business.com" />
              </div>
            </div>

            <div>
              <label className="block text-sm text-[#A1A1AA] mb-1.5 flex items-center gap-1"><Globe className="w-3 h-3" /> Website</label>
              <input value={form.website} onChange={(e) => update("website", e.target.value)} className="input-minimal w-full" placeholder="https://..." />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full py-3 text-sm font-medium disabled:opacity-50"
            data-testid="vendor-submit-btn"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</span>
            ) : (
              <span className="flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Register as Service Provider</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VendorOnboarding;
